"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LanguageStep } from "./steps/LanguageStep";
import { SettingsStep } from "./steps/SettingsStep";
import { TourStep } from "./steps/TourStep";
import { WelcomeStep } from "./steps/WelcomeStep";
import { useAuth } from "@/hooks/useAuth";

interface OnboardingWizardProps {
  isOpen: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

interface Step {
  id: string;
  title: string;
  description?: string;
}

const allSteps: Step[] = [
  {
    id: "language",
    title: "Choose Your Language",
    description: "Select your preferred language · Elige tu idioma preferido",
  },
  {
    id: "welcome",
    title: "Welcome to JobWright",
    description: "Let's get you started with your new workspace",
  },
  {
    id: "features",
    title: "Your Toolkit",
    description: "Discover the powerful features available to you",
  },
  {
    id: "settings",
    title: "Customize Your Experience",
    description: "Enable the features that matter most to your business",
  },
  {
    id: "tour",
    title: "Quick Tour",
    description: "See how everything works together",
  },
  {
    id: "start",
    title: "Ready to Go!",
    description: "You're all set to start using JobWright",
  },
];

export function OnboardingWizard({
  isOpen,
  onComplete,
  onSkip,
}: OnboardingWizardProps) {
  const { isOwner } = useAuth();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [direction, setDirection] = React.useState<"forward" | "backward">(
    "forward"
  );

  // Filter steps based on user role - staff don't see settings step
  const steps = React.useMemo(() => {
    if (isOwner) {
      return allSteps;
    }
    // Remove settings step for staff
    return allSteps.filter(step => step.id !== "settings");
  }, [isOwner]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setDirection("forward");
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection("backward");
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  const handleStepClick = (index: number) => {
    if (index < currentStep) {
      setDirection("backward");
    } else if (index > currentStep) {
      setDirection("forward");
    }
    setCurrentStep(index);
  };

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  // Reset to first step when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setDirection("forward");
    }
  }, [isOpen]);

  // Animation variants for step transitions
  const slideVariants = {
    enter: (direction: "forward" | "backward") => ({
      x: direction === "forward" ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: "forward" | "backward") => ({
      x: direction === "forward" ? -1000 : 1000,
      opacity: 0,
    }),
  };

  // Render step content based on current step
  const renderStepContent = (stepId: string) => {
    switch (stepId) {
      case "language":
        return (
          <LanguageStep
            onNext={handleNext}
            onBack={handleBack}
            isFirst={isFirstStep}
            isLast={isLastStep}
          />
        );

      case "welcome":
        return (
          <WelcomeStep
            onNext={handleNext}
            onBack={handleBack}
            isFirst={isFirstStep}
            isLast={isLastStep}
          />
        );

      case "features":
        return (
          <div className="flex flex-col h-full">
            <p className="text-gray-600 mb-6">
              JobWright provides powerful tools to manage your field service business:
            </p>
            <div className="grid grid-cols-2 gap-4">
              <FeatureCard
                icon="📋"
                title="Job Management"
                description="Track jobs, estimates, and invoices"
              />
              <FeatureCard
                icon="⏱️"
                title="Time Tracking"
                description="Monitor team hours and productivity"
              />
              <FeatureCard
                icon="💬"
                title="Two-Way SMS"
                description="Communicate with customers instantly"
              />
              <FeatureCard
                icon="📍"
                title="GPS & Dispatch"
                description="Track technicians and optimize routes"
              />
              <FeatureCard
                icon="💰"
                title="Price Book"
                description="Manage your services and pricing"
              />
              <FeatureCard
                icon="📊"
                title="Reporting"
                description="Gain insights into your business"
              />
            </div>
          </div>
        );

      case "settings":
        return (
          <SettingsStep
            onNext={handleNext}
            onBack={handleBack}
            isFirst={isFirstStep}
            isLast={isLastStep}
          />
        );

      case "tour":
        return (
          <TourStep
            onNext={handleNext}
            onBack={handleBack}
            isFirst={isFirstStep}
            isLast={isLastStep}
          />
        );

      case "start":
        return (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-12 h-12 text-success"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              You're All Set!
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mb-6">
              Your workspace is ready to go. Start by creating your first job,
              adding team members, or exploring the features we've enabled for you.
            </p>
            <div className="flex flex-col gap-2 text-sm text-gray-600 bg-info/10 p-4 rounded-lg">
              <p className="font-semibold text-blue-900">Quick tips:</p>
              <ul className="text-left space-y-1">
                <li>• Use the dashboard to see an overview of your business</li>
                <li>• Check out the help center if you need guidance</li>
                <li>• Customize your settings anytime from your profile</li>
              </ul>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Step content coming soon...</p>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] p-0 gap-0 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b">
          <DialogHeader className="p-0 space-y-0">
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {currentStepData.title}
            </DialogTitle>
            {currentStepData.description && (
              <p className="text-sm text-gray-600 mt-1">
                {currentStepData.description}
              </p>
            )}
          </DialogHeader>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 px-6 py-4 bg-gray-50">
          {steps.map((step, index) => (
            <button
              key={step.id}
              onClick={() => handleStepClick(index)}
              className={cn(
                "transition-all duration-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                index === currentStep
                  ? "w-10 h-3 bg-info"
                  : index < currentStep
                  ? "w-3 h-3 bg-blue-400 hover:bg-info"
                  : "w-3 h-3 bg-gray-300 hover:bg-gray-400"
              )}
              aria-label={`Step ${index + 1}: ${step.title}`}
              aria-current={index === currentStep ? "step" : undefined}
            />
          ))}
        </div>

        {/* Step Content with Animation */}
        <div className="relative flex-1 overflow-y-auto min-h-[400px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              className="absolute inset-0 p-8"
            >
              {/* Step Content Placeholder */}
              {renderStepContent(currentStepData.id)}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer with Navigation (hidden for welcome, settings and tour steps that have their own navigation) */}
        {currentStepData.id !== "language" && currentStepData.id !== "welcome" && currentStepData.id !== "settings" && currentStepData.id !== "tour" && (
          <div className="flex items-center justify-between gap-4 p-6 pt-4 border-t bg-white">
            <div className="flex-1">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={handleSkip}>
                Skip Tour
              </Button>
              <Button
                onClick={handleNext}
                className="bg-info hover:bg-blue-700 text-white min-w-[120px]"
              >
                {isLastStep ? (
                  "Get Started"
                ) : (
                  <>
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Helper component for feature cards
function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) {
  return (
    <div className="p-4 border border-gray-200 rounded-lg hover:border-info hover:bg-blue-50/50 transition-colors">
      <div className="text-3xl mb-2">{icon}</div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}
